import { Box, Flex, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiArrowRight } from 'react-icons/fi';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';

import {
  getDataSourceApiPreviewEnabled,
  getNoheadersStatus,
  getSelectedDataFileDetails,
  getSelectedVersionDetails,
  getTableHeader,
  handleActionStepNext,
  resetSingleTimePeriodValue,
  setClearSelectedAreasTable,
  setMultiTimePeriodValue,
  setSelectedAreasProperties,
  setTimePeriodRowIndex,
} from 'redux/DataImportSlice';
import { getModelTime } from 'redux/IndicatorPageSlice';
import { PLAN_TEXT } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';

import colors from '../../../dataTheme';
import ActionStepFooter from '../../ActionStepFooter';
import {
  FieldError,
  FieldLabel,
  StepCheckbox,
  StepFooterRow,
  StepHeading,
  StepNotice,
  StepSelect,
} from '../../actionStepUi';

import { loadTimeRowOptions, type TimeColumnMapping, type TimeRowOption } from './timeRowOptions';

interface Properties {
  setHelpModalSeleceted?: any;
  getFormValue?: any;
}

const validationSchema = Yup.object().shape({
  selectedRowIndex: Yup.string().required('A time period row is required'),
});

type TimePeriodFormValues = {
  selectedRowIndex: string;
  periodMappings: TimeColumnMapping[];
};

const applySavedMappings = (
  mappings: TimeColumnMapping[],
  savedMappings: Array<{ key: string; value: string | null }> | null | undefined,
  modelPeriods: string[],
) => {
  if (!Array.isArray(savedMappings)) return mappings;

  const validModelPeriods = new Set(modelPeriods.map(String));
  const savedByColumn = new Map(
    savedMappings.map((mapping) => [mapping.key, mapping.value ?? null]),
  );
  return mappings.map((mapping) => {
    if (!savedByColumn.has(mapping.key)) return mapping;

    const savedValue = savedByColumn.get(mapping.key);
    if (!savedValue || !validModelPeriods.has(String(savedValue))) return mapping;

    return {
      ...mapping,
      value: savedValue,
      autoMapped: true,
    };
  });
};

const getMappedPeriodCount = (mappings: TimeColumnMapping[]) =>
  mappings.filter((mapping) => Boolean(mapping.value)).length;

/** Keeps CSV preview ticks/highlights in sync with mapped time columns. */
const TimePeriodColumnHighlighter = ({
  loadingRows,
  periodMappings,
}: {
  loadingRows: boolean;
  periodMappings: TimeColumnMapping[];
}) => {
  const dispatch: any = useDispatch();
  const mappedHeadersKey = periodMappings
    .filter((entry) => entry.key && entry.value)
    .map((entry) => entry.key)
    .join('\0');

  useEffect(() => {
    if (loadingRows) return;

    const mappedHeaders = mappedHeadersKey ? mappedHeadersKey.split('\0') : [];
    const payload: Record<string, string> = { selectedColumnOption: '' };
    mappedHeaders.forEach((header, index) => {
      payload[`timePeriodMap.${index}`] = header;
    });

    dispatch(setClearSelectedAreasTable());
    dispatch(setSelectedAreasProperties(payload));
  }, [dispatch, loadingRows, mappedHeadersKey]);

  return null;
};

const Columns = ({ getFormValue }: Properties) => {
  const dispatch: any = useDispatch();
  const headerNames = useSelector(getTableHeader);
  const selectedVersion = useSelector(getSelectedVersionDetails);
  const selectedFile = useSelector(getSelectedDataFileDetails);
  const useApiPreview = useSelector(getDataSourceApiPreviewEnabled);
  const modelTime = useSelector(getModelTime);
  const noHeaders = useSelector(getNoheadersStatus);
  const [rowOptions, setRowOptions] = useState<TimeRowOption[]>([]);
  // Start true so we don't flash an empty/error state before the first load runs.
  const [loadingRows, setLoadingRows] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [manualMapping, setManualMapping] = useState(false);
  const modelPeriods = useMemo(
    () => (Array.isArray(modelTime?.time_range) ? modelTime.time_range : []),
    [modelTime?.time_range],
  );

  useEffect(() => {
    let isCancelled = false;

    const loadRowCandidates = async () => {
      if (!selectedVersion?.s3_file_url || !headerNames?.length || modelPeriods.length === 0) {
        setRowOptions([]);
        // Stay in loading while prerequisites are still resolving (e.g. model time).
        setLoadingRows(true);
        setLoadError('');
        return;
      }

      setLoadingRows(true);
      setLoadError('');

      try {
        const options = await loadTimeRowOptions({
          s3FileUrl: selectedVersion.s3_file_url,
          headerNames,
          modelTimeRange: modelPeriods,
          dataSourceId: selectedFile?.id,
          versionId: selectedVersion?.id,
          useApiPreview,
        });
        if (isCancelled) return;
        setRowOptions(options);
        setLoadingRows(false);
      } catch {
        if (isCancelled) return;
        setRowOptions([]);
        setLoadError('Unable to read the selected file for time period detection.');
        setLoadingRows(false);
      }
    };

    loadRowCandidates();

    return () => {
      isCancelled = true;
    };
  }, [
    headerNames,
    modelPeriods,
    noHeaders,
    selectedVersion?.s3_file_url,
    selectedVersion?.id,
    selectedFile?.id,
    useApiPreview,
  ]);

  const initialFormValues = useMemo<TimePeriodFormValues>(() => {
    const saved = getFormValue?.timePeriodRowIndex;
    let selectedRowIndex = '';
    if (saved != null && rowOptions.some((row) => row.index === Number(saved))) {
      selectedRowIndex = String(saved);
    } else if (rowOptions.length > 0) {
      const bestRow = rowOptions.reduce((best, current) => {
        if (current.matchedCount !== best.matchedCount) {
          return current.matchedCount > best.matchedCount ? current : best;
        }
        return current.detectedCount > best.detectedCount ? current : best;
      });
      selectedRowIndex = String(bestRow.index);
    }

    const selectedRow = rowOptions.find((row) => String(row.index) === String(selectedRowIndex));
    return {
      selectedRowIndex,
      periodMappings: applySavedMappings(
        selectedRow?.mappings || [],
        getFormValue?.multiTimePeriodValue,
        modelPeriods,
      ),
    };
  }, [
    getFormValue?.multiTimePeriodValue,
    getFormValue?.timePeriodRowIndex,
    modelPeriods,
    rowOptions,
  ]);

  const staleSavedMappingCount = useMemo(() => {
    if (!Array.isArray(getFormValue?.multiTimePeriodValue)) return 0;

    const validModelPeriods = new Set(modelPeriods.map(String));
    return getFormValue.multiTimePeriodValue.filter(
      (mapping: { value?: string | null }) =>
        mapping?.value && !validModelPeriods.has(String(mapping.value)),
    ).length;
  }, [getFormValue?.multiTimePeriodValue, modelPeriods]);

  const handleSubmit = async (
    form: TimePeriodFormValues,
    setSubmitting: (data: boolean) => void,
  ) => {
    const selectedRow = rowOptions.find(
      (row) => String(row.index) === String(form.selectedRowIndex),
    );
    const mappingsForPayload = (form.periodMappings || [])
      .filter((mapping) => mapping?.key && mapping?.value)
      .map((mapping) => ({
        key: mapping.key,
        value: mapping.value,
      }));

    if (!selectedRow || mappingsForPayload.length === 0) {
      setSubmitting(false);
      return;
    }

    await dispatch(setTimePeriodRowIndex(selectedRow.index));
    await dispatch(setMultiTimePeriodValue(mappingsForPayload));
    await dispatch(resetSingleTimePeriodValue());
    await setSubmitting(false);
    await dispatch(handleActionStepNext());
  };

  return (
    <Box w='100%'>
      <StepNotice mb={4}>
        Select the row which has the time periods for each column. We&apos;ll auto-map any matching
        period labels to the model timeline.
      </StepNotice>
      <Formik
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
        enableReinitialize={true}
      >
        {({ values, errors, setFieldValue, isSubmitting }: any) => {
          const rowPlaceholder = loadingRows ? 'Detecting rows…' : 'Select row';
          const periodMappings: TimeColumnMapping[] = values?.periodMappings || [];
          const mappedPeriodCount = getMappedPeriodCount(periodMappings);
          const hasUnmappedPeriods = periodMappings.some((mapping) => !mapping.value);
          const mappingControlsEnabled = manualMapping || hasUnmappedPeriods;
          const updatePeriodMapping = async (mappingIndex: number, period: string) => {
            const nextMappings = periodMappings.map((mapping, index) =>
              index === mappingIndex
                ? {
                    ...mapping,
                    value: period || null,
                    autoMapped: false,
                  }
                : mapping,
            );
            await setFieldValue('periodMappings', nextMappings);
          };

          return (
            <Form style={{ width: '100%' }}>
              <TimePeriodColumnHighlighter
                loadingRows={loadingRows}
                periodMappings={periodMappings}
              />
              <FieldLabel>Time period row</FieldLabel>
              <StepSelect
                value={values?.selectedRowIndex}
                placeholder={rowPlaceholder}
                isInvalid={Boolean(errors.selectedRowIndex)}
                isDisabled={loadingRows || rowOptions.length === 0}
                options={rowOptions.map((row) => ({
                  value: String(row.index),
                  label: `${row.label} (${row.matchedCount}/${row.detectedCount} matched)`,
                }))}
                onChange={async (role) => {
                  await setFieldValue('selectedRowIndex', role);
                  const selectedRow = rowOptions.find((row) => String(row.index) === String(role));
                  await setFieldValue('periodMappings', selectedRow?.mappings || []);
                  setManualMapping(false);
                }}
              />
              <ErrorMessage name='selectedRowIndex' component={FieldError} />

              {periodMappings.length > 0 && (
                <Flex direction='column' gap={3} pt={5} w='100%'>
                  <StepCheckbox
                    isChecked={mappingControlsEnabled}
                    isDisabled={hasUnmappedPeriods}
                    onChange={setManualMapping}
                  >
                    Map manually
                  </StepCheckbox>

                  {hasUnmappedPeriods && (
                    <StepNotice tone='warning'>
                      Some detected headers do not match the model timeline. Select target periods
                      below; unmapped columns will be skipped.
                    </StepNotice>
                  )}

                  {staleSavedMappingCount > 0 && (
                    <StepNotice tone='warning'>
                      Some saved time period mappings are no longer in the current model timeline.
                      Please remap them before continuing.
                    </StepNotice>
                  )}

                  <StepHeading title='Map the values into Blox' />

                  <Flex alignItems='center' gap={3} mt='-8px'>
                    <FieldLabel flex={1} mb={0}>
                      Time period in file
                    </FieldLabel>
                    <Box w='16px' />
                    <FieldLabel flex={1} mb={0}>
                      Time period in Blox
                    </FieldLabel>
                  </Flex>

                  {periodMappings.map((mapping, index) => (
                    <Flex key={`${mapping.key}-${index}`} alignItems='center' gap={3} w='100%'>
                      <Text
                        flex={1}
                        minW={0}
                        {...PLAN_TEXT.bodyMedium}
                        color={colors.textDark}
                        noOfLines={1}
                        title={mapping.sourceValue || mapping.key}
                      >
                        {mapping.sourceValue || mapping.key}
                      </Text>
                      <FiArrowRight size={16} color={colors.textSecondary} />
                      <Box flex={1} minW={0}>
                        <StepSelect
                          value={mapping.value || ''}
                          placeholder='Select period'
                          isClearable
                          isDisabled={!mappingControlsEnabled}
                          options={modelPeriods.map((period: string) => ({
                            value: period,
                            label: period,
                          }))}
                          onChange={(period) => updatePeriodMapping(index, String(period))}
                        />
                      </Box>
                    </Flex>
                  ))}
                </Flex>
              )}

              {!loadingRows && rowOptions.length === 0 && (
                <StepNotice tone='danger' pt={2}>
                  No date-like time period rows were detected in the selected file.
                </StepNotice>
              )}

              {loadError && (
                <StepNotice tone='danger' pt={2}>
                  {loadError}
                </StepNotice>
              )}

              <StepFooterRow>
                <ActionStepFooter
                  nextButtonName='Continue'
                  prevButtonName='Previous'
                  isSubmitting={isSubmitting}
                  disableNext={loadingRows || rowOptions.length === 0 || mappedPeriodCount === 0}
                />
              </StepFooterRow>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default Columns;
